import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Link2, Check, Loader2, Search } from "lucide-react";

const inputCls = "flex-1 min-w-[180px] bg-[#121418] border border-[#272A34] rounded-md px-3 py-1.5 text-xs text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500";

export const WatchlistTourUrls = () => {
  const { data: list = [], refetch } = useQuery({ queryKey: ["watchlistFull"], queryFn: async () => (await api.get("/admin/watchlist")).data });
  const [edits, setEdits] = useState({});
  const [savingSlug, setSavingSlug] = useState(null);
  const [q, setQ] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);

  useEffect(() => {
    const m = {};
    list.forEach((a) => { m[a.slug] = a.official_tour_url || ""; });
    setEdits(m);
  }, [list]);

  const save = async (a) => {
    setSavingSlug(a.slug);
    try {
      await api.put(`/admin/watchlist/${a.slug}`, { official_tour_url: (edits[a.slug] || "").trim() });
      toast.success(`URL enregistrée · ${a.name}`);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setSavingSlug(null);
  };

  const filled = list.filter((a) => a.official_tour_url).length;
  const shown = list.filter((a) => {
    if (q && !a.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (onlyMissing && a.official_tour_url) return false;
    return true;
  });

  return (
    <div className="mb-4 rounded-md border border-[#272A34] bg-[#181A20] p-3" data-testid="watchlist-tour-urls">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Link2 className="w-4 h-4 text-amber-400" />
        <h4 className="font-serif text-sm text-amber-100">Sites de tournée officiels ({filled}/{list.length} renseignés)</h4>
      </div>
      <p className="text-[11px] text-stone-500 mb-3">Ajoutez l'URL de la page « tour / agenda » officielle d'un groupe. La veille concerts l'analysera pour trouver des dates au-delà de Ticketmaster.</p>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="w-3.5 h-3.5 text-stone-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input data-testid="wtu-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un groupe…" className="w-full bg-[#121418] border border-[#272A34] rounded-md pl-8 pr-3 py-1.5 text-xs text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" />
        </div>
        <label className="inline-flex items-center gap-1.5 text-xs text-stone-400 cursor-pointer">
          <input data-testid="wtu-only-missing" type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} className="accent-amber-600" /> Sans URL uniquement
        </label>
      </div>
      <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
        {shown.map((a) => (
          <div key={a.slug} data-testid={`wtu-row-${a.slug}`} className="flex items-center gap-2 flex-wrap">
            <span className="font-serif text-xs text-stone-200 w-32 truncate shrink-0">{a.name}</span>
            <input
              data-testid={`wtu-input-${a.slug}`}
              value={edits[a.slug] ?? ""}
              onChange={(e) => setEdits((p) => ({ ...p, [a.slug]: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && save(a)}
              placeholder="https://groupe.com/tour"
              className={inputCls}
            />
            <button data-testid={`wtu-save-${a.slug}`} onClick={() => save(a)} disabled={savingSlug === a.slug} className="inline-flex items-center gap-1 text-xs bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-2.5 py-1.5 rounded-md disabled:opacity-60">
              {savingSlug === a.slug ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WatchlistTourUrls;
